import { type TreeNode } from 'primereact/treenode';
import {
    type NormalizeAncestorsOnlySelectionInput,
    type RemoveSelectedValueInput,
    type TreeNodeMaps,
    type TreeSelectCheckboxSelectionState,
    type TreeSelectMultipleSelectionState,
    type TreeSelectSelectionMode,
    type TreeSelectValue,
} from '../models/tree-select-checkbox-selection-behavior';

function isMultipleSelectionState(
    value: TreeSelectValue,
): value is TreeSelectMultipleSelectionState {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCheckboxSelectionState(
    value: TreeSelectValue,
): value is TreeSelectCheckboxSelectionState {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getSelectedKeys(
    value: TreeSelectValue,
    selectionMode?: TreeSelectSelectionMode,
): string[] {
    if (selectionMode === 'single') {
        return typeof value === 'string' ? [value] : [];
    }

    if (selectionMode === 'multiple') {
        if (!isMultipleSelectionState(value)) {
            return [];
        }

        return Object.entries(value)
            .filter(([, state]) => Boolean(state))
            .map(([key]) => key);
    }

    return getCheckedKeys(value);
}

export function getCheckedKeys(value: TreeSelectValue): string[] {
    if (!isCheckboxSelectionState(value)) {
        return [];
    }

    return Object.entries(value)
        .filter(([, state]) => state.checked)
        .map(([key]) => key);
}

export function hasSameCheckedKeys(
    left: TreeSelectValue,
    right: TreeSelectValue,
): boolean {
    const leftCheckedKeys = new Set(getCheckedKeys(left));
    const rightCheckedKeys = new Set(getCheckedKeys(right));

    return (
        leftCheckedKeys.size === rightCheckedKeys.size &&
        [...leftCheckedKeys].every((key) => rightCheckedKeys.has(key))
    );
}

function buildSelection(
    checkedKeys: Set<string>,
): TreeSelectCheckboxSelectionState {
    const selection: TreeSelectCheckboxSelectionState = {};

    for (const key of checkedKeys) {
        selection[key] = { checked: true, partialChecked: false };
    }

    return selection;
}

function deleteDescendantKeys(
    selectedKeys: Set<string>,
    key: string,
    childrenByNodeKey: Map<string, string[]>,
): void {
    const stack = [...(childrenByNodeKey.get(key) ?? [])];

    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) {
            continue;
        }

        selectedKeys.delete(current);
        stack.push(...(childrenByNodeKey.get(current) ?? []));
    }
}

function addAncestorsToSelection(
    checkedKeys: Set<string>,
    key: string,
    parentByNodeKey: Map<string, string>,
): void {
    let current = parentByNodeKey.get(key);

    while (current) {
        checkedKeys.add(current);
        current = parentByNodeKey.get(current);
    }
}

export function canonicalizeAncestorsOnlySelection(
    value: TreeSelectValue,
    nodeMaps: TreeNodeMaps,
): TreeSelectCheckboxSelectionState {
    const checkedKeys = new Set(getCheckedKeys(value));

    for (const key of [...checkedKeys]) {
        addAncestorsToSelection(checkedKeys, key, nodeMaps.parentByNodeKey);
    }

    return buildSelection(checkedKeys);
}

function buildMultipleSelection(
    selectedKeys: Set<string>,
): TreeSelectMultipleSelectionState {
    const selection: TreeSelectMultipleSelectionState = {};

    for (const key of selectedKeys) {
        selection[key] = true;
    }

    return selection;
}

function buildCheckboxSelectionState(
    value: TreeSelectValue,
): TreeSelectCheckboxSelectionState {
    if (!isCheckboxSelectionState(value)) {
        return {};
    }

    const selection: TreeSelectCheckboxSelectionState = {};

    for (const [key, state] of Object.entries(value)) {
        selection[key] = {
            checked: Boolean(state.checked),
            partialChecked: Boolean(state.partialChecked),
        };
    }

    return selection;
}

function deleteNodeWithDescendantsFromSelection(
    selection: TreeSelectCheckboxSelectionState,
    key: string,
    childrenByNodeKey: Map<string, string[]>,
): void {
    delete selection[key];

    const stack = [...(childrenByNodeKey.get(key) ?? [])];

    while (stack.length > 0) {
        const current = stack.pop();
        if (!current) {
            continue;
        }

        delete selection[current];
        stack.push(...(childrenByNodeKey.get(current) ?? []));
    }
}

function updateAncestorSelectionState(
    selection: TreeSelectCheckboxSelectionState,
    key: string,
    nodeMaps: TreeNodeMaps,
): void {
    let current = nodeMaps.parentByNodeKey.get(key);

    while (current) {
        const childKeys = nodeMaps.childrenByNodeKey.get(current) ?? [];
        const hasCheckedChild = childKeys.some(
            (childKey) => selection[childKey]?.checked,
        );
        const hasPartialChild = childKeys.some(
            (childKey) => selection[childKey]?.partialChecked,
        );
        const isCompletelyChecked =
            childKeys.length > 0 &&
            childKeys.every((childKey) => selection[childKey]?.checked);

        if (isCompletelyChecked) {
            selection[current] = {
                checked: true,
                partialChecked: false,
            };
        } else if (hasCheckedChild || hasPartialChild) {
            selection[current] = {
                checked: false,
                partialChecked: true,
            };
        } else {
            delete selection[current];
        }

        current = nodeMaps.parentByNodeKey.get(current);
    }
}

function removeFromCheckboxSelection(
    value: TreeSelectValue,
    key: string,
    nodeMaps: TreeNodeMaps,
): TreeSelectCheckboxSelectionState {
    const selection = buildCheckboxSelectionState(value);

    deleteNodeWithDescendantsFromSelection(
        selection,
        key,
        nodeMaps.childrenByNodeKey,
    );
    updateAncestorSelectionState(selection, key, nodeMaps);

    return selection;
}

function removeFromMultipleSelection(
    value: TreeSelectValue,
    key: string,
): TreeSelectMultipleSelectionState {
    const selectedKeys = new Set(getSelectedKeys(value, 'multiple'));
    selectedKeys.delete(key);

    return buildMultipleSelection(selectedKeys);
}

export function removeSelectedValue({
    key,
    nodeMaps,
    selectionMode,
    value,
    preserveAncestorsOnlySelection = false,
}: RemoveSelectedValueInput): TreeSelectValue {
    if (selectionMode === 'multiple') {
        return removeFromMultipleSelection(value, key);
    }

    if (selectionMode !== 'checkbox') {
        return value;
    }

    if (preserveAncestorsOnlySelection) {
        const checkedKeys = new Set(getCheckedKeys(value));
        checkedKeys.delete(key);
        deleteDescendantKeys(checkedKeys, key, nodeMaps.childrenByNodeKey);

        return buildSelection(checkedKeys);
    }

    return removeFromCheckboxSelection(value, key, nodeMaps);
}

export function getSelectedNodes(
    value: TreeSelectValue,
    selectionMode: TreeSelectSelectionMode | undefined,
    nodeMaps: TreeNodeMaps | undefined,
): TreeNode[] {
    if (!nodeMaps) {
        return [];
    }

    const selectedKeys = new Set(getSelectedKeys(value, selectionMode));

    return nodeMaps.orderedNodeKeys
        .filter((key) => selectedKeys.has(key))
        .map((key) => nodeMaps.nodeByNodeKey.get(key))
        .filter((node): node is TreeNode => Boolean(node));
}

export function normalizeAncestorsOnlySelection({
    nextValue,
    previousValue,
    nodeMaps,
    interaction,
}: NormalizeAncestorsOnlySelectionInput): TreeSelectCheckboxSelectionState {
    if (!interaction) {
        return canonicalizeAncestorsOnlySelection(nextValue, nodeMaps);
    }

    const checkedKeys = new Set(getCheckedKeys(previousValue));

    if (interaction.action === 'select') {
        checkedKeys.add(interaction.key);
        addAncestorsToSelection(
            checkedKeys,
            interaction.key,
            nodeMaps.parentByNodeKey,
        );

        return buildSelection(checkedKeys);
    }

    checkedKeys.delete(interaction.key);
    deleteDescendantKeys(
        checkedKeys,
        interaction.key,
        nodeMaps.childrenByNodeKey,
    );

    return buildSelection(checkedKeys);
}

export function buildNodeMaps(nodes: TreeNode[] | undefined): TreeNodeMaps {
    const parentByNodeKey = new Map<string, string>();
    const childrenByNodeKey = new Map<string, string[]>();
    const nodeByNodeKey = new Map<string, TreeNode>();
    const orderedNodeKeys: string[] = [];

    const walk = (node: TreeNode, parent?: string): void => {
        const key = String(node.key);

        if (parent) {
            parentByNodeKey.set(key, parent);
        }

        nodeByNodeKey.set(key, node);
        orderedNodeKeys.push(key);
        childrenByNodeKey.set(
            key,
            (node.children ?? []).map((child) => String(child.key)),
        );

        for (const child of node.children ?? []) {
            walk(child, key);
        }
    };

    for (const node of nodes ?? []) {
        walk(node);
    }

    return {
        parentByNodeKey,
        childrenByNodeKey,
        nodeByNodeKey,
        orderedNodeKeys,
    };
}
